import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import useLink from "@qpr/client-api-base/hooks/useLink";
import useElement from "backend/hooks/elements/useElement";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import React from "react";
import Page from "design/pages/Page";
import PageContentMenu from "@qpr/client-api-base/uicomponents/view/PageContentMenu";
import { t } from "i18next";
import Anchor from "@qpr/client-api-base/ui/Anchor";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import OverallPerformanceCard from "design/components/cards/OverallPerformanceCard";
import ElementOverviewDonutCard from "design/components/cards/ElementOverviewDonutCard";
import useScorecardTopElement from "backend/hooks/model/useScorecardTopElement";
import flattenHierarchy from "backend/flattenHierarchy";
import KPIGridArea from "design/components/grids/MeasureGridArea";
import { InitiativesHeader, KpisHeader } from "design/components/texts/Headers";
import ExecutiveDashboardToolbar from "components/toolbars/ExecutiveDashboardToolbar";
import { IChartSelectEvent } from "design/components/charts/types";
import KPIDrillDown from "design/components/popups/KPIDrillDown";
import InitiativeDrillDown from "design/components/popups/InitiativeDrillDown";
import { InitiativeCard } from "design/components/cards/InitiativeCard";
import { KPICard } from "design/components/cards/KPICard";
import ExecutiveDashboardTitle from "design/components/texts/ExecutiveDashboardTitle";
import LeftMenuPosition from "design/layouts/LeftMenuPosition";

const ExecutiveDashboard = () => {
  const ctx = useAppContext();
  const link = useLink();

  const { data: theme } = useScorecardTopElement("L0");
  const { data: el } = useElement(theme?.id);
  const { data, isLoading: isThemesLoading } = useHierarchy(el?.id);

  const [showKpiModal, setShowKPIModal] =
    React.useState<IChartSelectEvent | null>(null);
  const [showInitiatives, setShowInitiatives] =
    React.useState<IChartSelectEvent | null>(null);

  const dashboardKpis = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return flattenHierarchy(data).filter(
      (elem) => elem.typename === "KPI" && !!elem.W
    );
  }, [data]);

  const corporateKpis = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return flattenHierarchy(data).filter((elem) => elem.typename === "KPI");
  }, [data]);
  const dashboardInitiatives = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return flattenHierarchy(data).filter(
      (elem) => elem.typename === "Initiative"
    );
  }, [data]);

  return (
    <Page
      isLoading={isThemesLoading || !el || !data}
      Toolbar={ExecutiveDashboardToolbar}
    >
      {showKpiModal?.category}
      <KPIDrillDown
        title={
          showKpiModal?.name === "No Value"
            ? "KPIs with no data"
            : `${showKpiModal?.name} performing KPIs`
        }
        showModal={showKpiModal}
        onClose={() => {
          setShowKPIModal(null);
        }}
        items={dashboardKpis}
      />

      <InitiativeDrillDown
        title={
          showInitiatives?.name === "No Value"
            ? "Inititiaves with no data"
            : `${showInitiatives?.name} performing Initiatives`
        }
        showModal={showInitiatives}
        onClose={() => {
          setShowInitiatives(null);
        }}
      />

      <SplitView left="150px" right="1200px" gap="40px">
        <div>
          <LeftMenuPosition>
            <PageContentMenu
              componentData={{
                items: [
                  { title: t("Dashboard Performance"), anchor: "header" },
                  { title: t("Dashboard KPIs"), anchor: "dashboard_kpis" },
                  { title: t("Corporate KPIs"), anchor: "corporate_kpis" },
                  {
                    title: t("Corporate Initiatives"),
                    anchor: "corporate_initiatives",
                  },
                ],
              }}
            />
          </LeftMenuPosition>
        </div>
        <div>
          <StackView>
            <Anchor name="header" />
            <ExecutiveDashboardTitle>
              Dashboard Performance YTD {el?.typename}
            </ExecutiveDashboardTitle>

            <SplitView partCnt={3} gap="35px">
              <OverallPerformanceCard
                key="PERF_W_YTD"
                titleKey="PERF_W_YTD_prettyvalue"
                element={el!}
                periodName={ctx.period || ""}
              />
              <ElementOverviewDonutCard
                title="KPI Overview"
                data={dashboardKpis!}
                typenames={["KPI"]}
                periodName={ctx.period || ""}
                onSelect={(event) => {
                  setShowKPIModal(event);
                }}
              />
              <ElementOverviewDonutCard
                title="Initiative Overview"
                data={dashboardInitiatives}
                typenames={["Initiative"]}
                periodName={ctx.period || ""}
                onSelect={(event) => {
                  setShowInitiatives(event);
                }}
              />
            </SplitView>

            <Anchor name="dashboard_kpis" />
            <KPIGridArea>
              <ExecutiveDashboardTitle>Dashboard KPIs</ExecutiveDashboardTitle>
              {data
                ? dashboardKpis.map((el) => (
                    <KPICard
                      valueKey="PERF_YTD"
                      prettyKey="PERF_YTD_prettyvalue"
                      colorKey="rangecolor_PERF_YTD_OPEN"
                      series={[
                        {
                          title: "Actual (YTD)",
                          series: "ACT_YTD_prettyvalue",
                        },
                        {
                          title: "Target (YTD)",
                          series: "TAR_YTD_prettyvalue",
                        },
                      ]}
                      width="285px"
                      height="280px"
                      period={ctx.period || ""}
                      key={`${el.id}-${ctx.period}`}
                      element={el}
                      onSelect={() => {
                        link({ page: "kpidetails", meaId: el.id });
                      }}
                    />
                  ))
                : null}
            </KPIGridArea>

            <Anchor name="corporate_kpis" />
            <KPIGridArea>
              <ExecutiveDashboardTitle>Corporate KPIs</ExecutiveDashboardTitle>
              {data
                ? corporateKpis.map((el) => (
                    <KPICard
                      valueKey="PERF_YTD"
                      prettyKey="PERF_YTD_prettyvalue"
                      colorKey="rangecolor_PERF_YTD_OPEN"
                      series={[
                        {
                          title: "Actual (YTD)",
                          series: "ACT_YTD_prettyvalue",
                        },
                        {
                          title: "Target (YTD)",
                          series: "TAR_YTD_prettyvalue",
                        },
                      ]}
                      width="285px"
                      height="280px"
                      period={ctx.period || ""}
                      key={`${el.id}-${ctx.period}`}
                      element={el}
                      onSelect={() => {
                        link({ page: "kpidetails", meaId: el.id });
                      }}
                    />
                  ))
                : null}
            </KPIGridArea>

            <Anchor name="corporate_initiatives" />

            <KPIGridArea>
              <ExecutiveDashboardTitle>
                Corporate Initiatives
              </ExecutiveDashboardTitle>

              {data
                ? dashboardInitiatives.map((el) => (
                    <InitiativeCard
                      width="280px"
                      height="280px"
                      left_number_key="PLANNED_COMPLETION_prettyvalue"
                      left_number_title="Planned Progress"
                      right_number_key="VARIANCE_prettyvalue"
                      right_number_title="Variance"
                      cardTitle="Actual Progress"
                      period={ctx.period || ""}
                      key={`${el.id}-${ctx.period}`}
                      element={el}
                      onSelect={() => {
                        link({ page: "initiativedetails", meaId: el.id });
                      }}
                    />
                  ))
                : null}
            </KPIGridArea>
          </StackView>
        </div>
      </SplitView>
    </Page>
  );
};

export default ExecutiveDashboard;
